// By default, this project supports all modern browsers.
// Support for Internet Explorer 11 requires polyfills.
// For to support Internet Explorer 11, install react-app-polyfill,
// https://github.com/facebook/create-react-app/tree/master/packages/react-app-polyfill
// import 'react-app-polyfill/ie11';
// import 'react-app-polyfill/stable';
import "typeface-muli";
// import "./react-chartjs-2-defaults";
import "./styles/index.css";
import React from "react";
import ReactDOM from "react-dom";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import axios from "axios";
import * as serviceWorker from "./serviceWorker";
import "./i18n";
import App from "./app/App";
import ErrorComponent from "./@catu/components/Error";

const BUILD_VERSION = process.env.REACT_APP_BUILD_VERSION || "dev";
console.log("BUILD_VERSION == ", BUILD_VERSION);

if (typeof window !== "undefined") {
  window.__APP_BUILD_VERSION__ = BUILD_VERSION;
}
// Détection de changement de version pour limiter les problèmes de cache navigateur
(() => {
  try {
    const STORAGE_KEY = "app_build_version";
    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }

    const savedVersion = window.localStorage.getItem(STORAGE_KEY);
    console.log("savedVersion == ", savedVersion);
    if (savedVersion && savedVersion !== BUILD_VERSION) {
      window.localStorage.setItem(STORAGE_KEY, BUILD_VERSION);

      const hardReload = async () => {
        try {
          if (typeof navigator !== "undefined" && navigator.serviceWorker) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((reg) => reg.unregister()));
          }

          if (typeof window.caches !== "undefined") {
            const cacheNames = await window.caches.keys();
            await Promise.all(
              cacheNames.map((cacheName) => window.caches.delete(cacheName))
            );
          }
        } catch (e) {
          // on ignore les erreurs de nettoyage
        }

        window.location.reload(true);
      };

      try {
        if (typeof window !== "undefined" && window.document) {
          const banner = window.document.createElement("div");
          banner.textContent =
            "Une nouvelle version de l'application est disponible. La page va se recharger et vous devrez vous reconnecter.";
          banner.style.position = "fixed";
          banner.style.top = "0";
          banner.style.left = "0";
          banner.style.right = "0";
          banner.style.zIndex = "9999";
          banner.style.backgroundColor = "#1976d2";
          banner.style.color = "#ffffff";
          banner.style.padding = "12px 16px";
          banner.style.textAlign = "center";
          banner.style.fontFamily = "sans-serif";
          banner.style.fontSize = "14px";

          window.document.body.appendChild(banner);

          // Laisser quelques secondes pour lire le message puis lancer le reload
          setTimeout(() => {
            hardReload();
          }, 5000);
        } else {
          // Fallback : si pas de DOM, on recharge directement
          hardReload();
        }
      } catch (e) {
        // En cas de problème d'affichage, on recharge quand même
        hardReload();
      }

      return;
    }

    if (!savedVersion) {
      window.localStorage.setItem(STORAGE_KEY, BUILD_VERSION);
    }
  } catch (e) {
    // on ignore les erreurs d'accès au storage
  }
})();

// Configuration de Sentry.
let dsn = "";
const environment = process.env.REACT_APP_NODE_ENV;

if (
  process.env.REACT_APP_SENTRY_DSN
) {
  dsn =
    process.env.REACT_APP_SENTRY_DSN ||
    "https://a05c7e9f933ab98d61478fceec26e99a@sentry.globetechnologie.com/6";
}

if (dsn) {
  // Interceptor global pour supprimer les headers Sentry des requêtes axios
  // Cela évite les erreurs CORS car le backend n'autorise pas ces headers
  axios.interceptors.request.use((config) => {
    if (config.headers) {
      // Supprimer les headers Sentry qui causent des erreurs CORS
      delete config.headers.baggage;
      delete config.headers["sentry-trace"];
    }
    return config;
  });

  // Déterminer les origines à tracer selon l'environnement
  // Utiliser l'origine actuelle pour supporter tous les environnements (dev, staging, beta, production)
  const getTracingOrigins = () => {
    if (typeof window !== "undefined") {
      const origin = window.location.origin;
      // Tracer l'origine actuelle et les chemins relatifs
      return [origin, /^\//];
    }
    // Fallback pour SSR ou si window n'est pas disponible
    return [/^\//];
  };

  Sentry.init({
    dsn,
    environment,
    release: BUILD_VERSION,
    integrations: [
      new BrowserTracing({
        // Tracer les routes React pour tous les environnements
        // Utilise l'origine actuelle (localhost, staging, beta, production, etc.)
        tracingOrigins: getTracingOrigins(),
        // Ne pas propager les headers de tracing (baggage, sentry-trace) vers les requêtes HTTP
        // Cela évite les erreurs CORS avec l'API backend
        tracePropagationTargets: [],
        // Désactiver le tracing des requêtes HTTP (XHR/fetch) pour éviter les problèmes CORS
        // Le tracing des routes React continuera de fonctionner
        shouldCreateSpanForRequest: (url) => {
          // Ne pas créer de span pour les requêtes HTTP
          return false;
        },
      }),
    ],
    // Taux d'échantillonnage des traces de performance (1.0 = 100%, 0.1 = 10%)
    tracesSampleRate: environment === "production" ? 0.1 : 1.0,
    // Options supplémentaires
    beforeSend(event, hint) {
      // Filtrer les erreurs sensibles si nécessaire
      // Vous pouvez ajouter ici une logique pour filtrer certains types d'erreurs
      return event;
    },
    // Ignorer certaines erreurs courantes qui ne sont pas critiques
    ignoreErrors: [
      // Erreurs de réseau courantes
      "NetworkError",
      "Failed to fetch",
      "Network request failed",
      // Erreurs de plugins navigateur
      "ResizeObserver loop limit exceeded",
      "Non-Error promise rejection captured",
    ],
    // Tags personnalisés pour faciliter le filtrage dans Sentry
    initialScope: {
      tags: {
        component: "frontend",
        build_version: BUILD_VERSION,
      },
    },
  });

  // Fonction de test pour vérifier que Sentry fonctionne
  // Accessible depuis la console du navigateur: window.testSentry()
  if (typeof window !== "undefined") {
    window.testSentry = function() {
      try {
        throw new Error("Test Sentry - My first Sentry error!");
      } catch (error) {
        Sentry.captureException(error);
        console.log("✅ Erreur de test envoyée à Sentry!");
        console.log("Vérifiez votre tableau de bord Sentry pour voir l'erreur.");
      }
    };
  }
}

// Composant de fallback personnalisé pour ErrorBoundary
const SentryErrorFallback = ({ error, resetError }) => {
  return (
    <ErrorComponent
      colNumber={1}
      retry={resetError}
    />
  );
};

ReactDOM.render(
  <Sentry.ErrorBoundary fallback={SentryErrorFallback}>
    <App />
  </Sentry.ErrorBoundary>,
  document.getElementById("root")
);
// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();

// test sylvainh 27/01/2026 15h57